import { useCallback, useMemo } from 'react';

import { type AirActionTrackingLocation, type UseTrackCanvasCreatedParams } from '@air/analytics';

import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { type ImportOptions } from '~/constants/MenuOptions';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { useCreateRootBoard } from '~/hooks/useCreateRootBoard';
import { useExposeImportSources } from '~/hooks/useExposeImportSources';
import { useImportFrom } from '~/hooks/useImportFrom';
import { useStoragePlanRestriction } from '~/hooks/useStoragePlanRestriction';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useUploadFilesFromComputer } from '~/hooks/useUploadFilesFromComputer';
import { useUploadFoldersFromComputer } from '~/hooks/useUploadFoldersFromComputer';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { getAddMenuOptions } from '~/utils/dropdownMenuOptions/getAddMenuOptions';
import { getCreateMenuOptions } from '~/utils/dropdownMenuOptions/getCreateMenuOptions';
import { getActionMenuOptions } from '~/utils/menuOptions/getActionMenuOptions';
import { canCreateAsset } from '~/utils/permissions/assetPermissions';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';
import { canCreateCanvasInWorkspace } from '~/utils/permissions/workspacePermissions';

export type UseAllContentActionsParams = {
  trackLocation: AirActionTrackingLocation;
  canvasSource?: UseTrackCanvasCreatedParams['source'];
};

export const useAllContentActions = ({
  trackLocation,
  canvasSource = 'all_content_header',
}: UseAllContentActionsParams) => {
  const { uploadFilesFromComputer } = useUploadFilesFromComputer({ trackLocation });
  const { uploadFoldersFromComputer } = useUploadFoldersFromComputer({ trackLocation });
  const { importFrom } = useImportFrom({ trackLocation });
  const { showCreateBoardModal } = useCreateRootBoard({ trackLocation });
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { openDraftCanvas } = useDraftCanvas();
  const isRightPanelOpen = useWorkspaceRightPanelOpen();
  const canCreateBoards = canCreateBoard(workspacePermissions);
  const canAddAssets = canCreateAsset(workspacePermissions);
  const canAddCanvas = canCreateCanvasInWorkspace(workspacePermissions);
  const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
  const { exposeImportSources } = useExposeImportSources();
  const { isStoragePlanRestricted, showStoragePlanRestrictionModal } = useStoragePlanRestriction();

  const onImportFromClick = useCallback(
    (importOption: ImportOptions) => {
      if (isStoragePlanRestricted) {
        showStoragePlanRestrictionModal();
      } else {
        importFrom(importOption);
      }
    },
    [importFrom, isStoragePlanRestricted, showStoragePlanRestrictionModal],
  );

  const onUploadFilesFromComputerClick = useCallback(() => {
    if (isStoragePlanRestricted) {
      showStoragePlanRestrictionModal();
    } else {
      uploadFilesFromComputer();
    }
  }, [uploadFilesFromComputer, isStoragePlanRestricted, showStoragePlanRestrictionModal]);

  const onUploadFoldersFromComputerClick = useCallback(() => {
    if (isStoragePlanRestricted) {
      showStoragePlanRestrictionModal();
    } else {
      uploadFoldersFromComputer();
    }
  }, [uploadFoldersFromComputer, isStoragePlanRestricted, showStoragePlanRestrictionModal]);

  const onCreateCanvasClick = useCallback(() => {
    openDraftCanvas({
      analyticsContext: { source: isRightPanelOpen ? 'all_content_canvas_open' : canvasSource },
    });
  }, [canvasSource, isRightPanelOpen, openDraftCanvas]);

  const canShowCreateCanvas = generativeCanvasEnabled && canAddCanvas;

  const allContentActionsOptions = useMemo(
    () =>
      getAddMenuOptions({
        onCreateBoard: canCreateBoards ? showCreateBoardModal : undefined,
        onCreateCanvas: canShowCreateCanvas ? onCreateCanvasClick : undefined,
        onImportFrom: canCreateBoards && canAddAssets ? onImportFromClick : undefined,
        onUploadFilesFromComputer: canAddAssets ? onUploadFilesFromComputerClick : undefined,
        onUploadFoldersFromComputer: canCreateBoards && canAddAssets ? onUploadFoldersFromComputerClick : undefined,
        exposeImportSources,
      }),
    [
      canAddAssets,
      canCreateBoards,
      canShowCreateCanvas,
      showCreateBoardModal,
      onCreateCanvasClick,
      onImportFromClick,
      onUploadFilesFromComputerClick,
      onUploadFoldersFromComputerClick,
      exposeImportSources,
    ],
  );

  const options = useMemo(
    () =>
      getActionMenuOptions({
        onCreateBoard: canCreateBoards ? showCreateBoardModal : undefined,
        onCreateCanvas: canShowCreateCanvas ? onCreateCanvasClick : undefined,
        onImportFrom: canCreateBoards && canAddAssets ? onImportFromClick : undefined,
        onUploadFilesFromComputer: canAddAssets ? onUploadFilesFromComputerClick : undefined,
        onUploadFoldersFromComputer: canCreateBoards && canAddAssets ? onUploadFoldersFromComputerClick : undefined,
        exposeImportSources,
      }),
    [
      canAddAssets,
      canCreateBoards,
      canShowCreateCanvas,
      showCreateBoardModal,
      onCreateCanvasClick,
      onImportFromClick,
      onUploadFilesFromComputerClick,
      onUploadFoldersFromComputerClick,
      exposeImportSources,
    ],
  );

  const createOptions = useMemo(
    () =>
      getCreateMenuOptions({
        onCreateBoard: canCreateBoards ? showCreateBoardModal : undefined,
        onCreateCanvas: canShowCreateCanvas ? onCreateCanvasClick : undefined,
      }),
    [canCreateBoards, canShowCreateCanvas, showCreateBoardModal, onCreateCanvasClick],
  );

  const uploadOptions = useMemo(
    () =>
      getAddMenuOptions({
        onImportFrom: canCreateBoards && canAddAssets ? onImportFromClick : undefined,
        onUploadFilesFromComputer: canAddAssets ? onUploadFilesFromComputerClick : undefined,
        onUploadFoldersFromComputer: canCreateBoards && canAddAssets ? onUploadFoldersFromComputerClick : undefined,
        exposeImportSources,
      }),
    [
      canAddAssets,
      canCreateBoards,
      onImportFromClick,
      onUploadFilesFromComputerClick,
      onUploadFoldersFromComputerClick,
      exposeImportSources,
    ],
  );

  return { allContentActionsOptions, options, createOptions, uploadOptions };
};
